import { useCallback, useMemo } from 'react';

import {
  createSpec,
  specTypes,
  useFetch,
  useDataRefId,
  useRefetch,
} from '@bingads-webui-react/overreact';

import { environmentLookupFn } from '../../../environment/environment-instance';
import {
  fetchRequestContract,
  fetchResponseContract,
} from '../spec/coll-fetch';
import type {
  Account,
  AccountsVariables,
  FetchAndRefetchConfig,
  ReadParameters,
  ReadRequestConfig,
} from '../../../types';

const fetchSpec = createSpec(
  fetchRequestContract, fetchResponseContract,
  specTypes.FETCH, null, environmentLookupFn
);
const refetchSpec = createSpec(
  fetchRequestContract, fetchResponseContract,
  specTypes.REFETCH, null, environmentLookupFn
);

const DEFAULT_CONFIG = {};


export function useAccounts(
  dataLocator: AccountsVariables,
  config: FetchAndRefetchConfig<Account, AccountsVariables> = DEFAULT_CONFIG
) {
  const dataRefId = useDataRefId(null, environmentLookupFn);

  const defaultConfig = useMemo(() => ({

    fetch: config.fetch || {},

    refetch: config.refetch || {},
  }), [config]);

  const { fetch: fetchConfig, refetch } = defaultConfig;

  const fetch = useFetch(dataRefId, fetchSpec, dataLocator, fetchConfig) as [
    Account[] | undefined,
    unknown,
  ];
  const refetchFn = useRefetch(dataRefId, refetchSpec, refetch);

  const read = useCallback((
    param: ReadParameters<AccountsVariables> = {},
    requestConfig?: ReadRequestConfig<Account, AccountsVariables>,
    ...rest: Array<ReadRequestConfig<Account, AccountsVariables> | undefined>
  ) => {
    refetchFn({
      variables: { ...dataLocator, ...param.variables },
    }, requestConfig, ...rest);
  }, [dataLocator, refetchFn]);

  const [data, error] = fetch;

  return [{ data, error }, { read }];
}
